<script lang="ts">
  import { formatHHMMSS } from '../../lib/format';
  import type { ChipTone } from '../chip-tone';
  import type { DeparturesState } from '../../stores/departures-response.svelte';
  import Chip from '../chip.svelte';
  import ChipStrip from '../chip-strip.svelte';

  interface Properties {
    /** Data-pipeline identifier the API returned; e.g. `"hafas"`. */
    source: string;
    /** UNIX-epoch seconds; `undefined` before the first successful fetch. */
    fetchedAt: number | undefined;
    /** Lifecycle of the board this strip sits above. */
    state: DeparturesState;
  }

  const { source, fetchedAt, state: boardState }: Properties = $props();

  /** `HH:MM:SS` of the last successful fetch, or `''` when there has been none. */
  const lastSuccess = $derived(
    fetchedAt === undefined ? '' : formatHHMMSS(new Date(fetchedAt * 1000)),
  );

  /** Chip text and tone for one lifecycle state, given the last successful fetch. */
  interface StatusChip {
    /** Text the chip prints; `''` renders no chip. */
    text: string;
    /** Tone the chip carries. */
    tone: ChipTone;
  }

  /**
   * Chip per lifecycle state. `fetchedAt` moves only on success, so only `ready`
   * labels it "Updated"; the other states name the fetch still on screen.
   */
  const STATUS_CHIP: Record<DeparturesState, (lastFetch: string) => StatusChip> = {
    idle: () => ({ text: '', tone: 'default' }),
    loading: (lastFetch) => ({
      text: lastFetch ? `Updating… — showing ${lastFetch}` : 'Updating…',
      tone: 'default',
    }),
    ready: (lastFetch) => ({ text: lastFetch ? `Updated ${lastFetch}` : '', tone: 'default' }),
    error: (lastFetch) => ({
      text: lastFetch ? `Refresh failed — showing ${lastFetch}` : 'Refresh failed',
      tone: 'warn',
    }),
  };

  const status = $derived(STATUS_CHIP[boardState](lastSuccess));
</script>

<!-- Two-chip strip above the board: which upstream we are reading, and the state of the
     most recent refresh. The timestamp reads "Updated" only when the board on screen is
     that fetch; a pending or failed refresh names the fetch it is still showing. -->
<ChipStrip>
  <Chip tone="accent">{source}</Chip>
  {#if status.text !== ''}
    <Chip tone={status.tone}>{status.text}</Chip>
  {/if}
</ChipStrip>
